import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';

export class Character {
  scene: Phaser.Scene;
  container: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  head: Phaser.GameObjects.Image;

  worldX: number = GAME_CONFIG.CHAR_CENTER_X;
  worldY: number = GAME_CONFIG.CHAR_CENTER_Y;
  worldZ: number = 0;

  currentHeadKey: string = 'head_main';

  private readonly originX: number;
  private readonly originY: number;
  private readonly scale: number;

  private lateralOffset = 0;
  private depthOffset = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.scale = GAME_CONFIG.CHAR_SCALE;
    this.originX = GAME_CONFIG.HEAD_PIVOT_X / GAME_CONFIG.HEAD_SOURCE_W;
    this.originY = GAME_CONFIG.HEAD_PIVOT_Y / GAME_CONFIG.HEAD_SOURCE_H;

    this.body = scene.add.image(0, 0, 'body');
    this.body.setOrigin(this.originX, this.originY);
    this.body.setScale(this.scale);

    this.head = scene.add.image(0, 0, 'head_main');
    this.head.setOrigin(this.originX, this.originY);
    this.head.setScale(this.scale);

    this.container = scene.add.container(this.worldX, this.worldY, [this.body, this.head]);
    this.container.setDepth(10);
  }

  setHead(headKey: string) {
    this.head.setTexture(headKey);
    this.currentHeadKey = headKey;
  }

  setHeadAngle(degrees: number) {
    this.head.setAngle(degrees);
  }

  setLateralOffset(dx: number) {
    this.lateralOffset = dx;
    this.worldX = GAME_CONFIG.CHAR_CENTER_X + dx;
    this.container.x = this.worldX;
  }

  setDepthOffset(dy: number) {
    this.depthOffset = dy;
    this.worldY = GAME_CONFIG.CHAR_CENTER_Y + dy;
    this.container.y = this.worldY;
  }

  /** Swap head to the sad face for `durationMs`, then restore the previous head
   * unless something else changed it in the meantime. Used by the miss reaction.
   *
   * If a blink was active when this fires, treat the previous state as
   * head_main rather than head_eyeclosed — otherwise the sad-face restore
   * leaves the character permanently with closed eyes, because BlinkSystem
   * only re-opens when it sees its own 'head_eyeclosed' marker. */
  showFailFace(durationMs: number = 900) {
    const prevKey =
      this.currentHeadKey === 'head_eyeclosed' ? 'head_main' : this.currentHeadKey;
    this.setHead('head_sad');
    this.scene.time.delayedCall(durationMs, () => {
      if (this.currentHeadKey === 'head_sad') {
        this.setHead(prevKey);
      }
    });
  }

  getHeadWorldPos() {
    return {
      x: this.worldX,
      y: this.worldY + GAME_CONFIG.HEAD_HIT_OFFSET_Y,
      z: this.worldZ,
      radius: GAME_CONFIG.HEAD_RADIUS,
      perfectRadius: GAME_CONFIG.PERFECT_RADIUS,
    };
  }

  reset() {
    this.setHead('head_main');
    this.head.setAngle(0);
    this.setLateralOffset(0);
    this.setDepthOffset(0);
  }
}
